/**
 * Upload service
 * Handles all file upload related API calls
 */

import { API_ENDPOINTS } from './endpoints';
import { fetchApi } from '@/lib/utils/api';

export interface UploadResponse {
  url: string;
}

export interface UploadOptions {
  tag?: string;
  secure?: boolean;
  expires?: number;
  maxSize?: number; // in bytes
  allowedTypes?: string[];
}

const DEFAULT_OPTIONS: UploadOptions = {
  secure: false,
  expires: 60, // 60 minutes
  maxSize: 5 * 1024 * 1024, // 5MB
  allowedTypes: ['image/jpeg', 'image/png', 'image/webp'],
};

export const uploadService = {
  /**
   * Upload a file
   * @param file - File to upload
   * @param options - Upload options
   * @returns Promise with the uploaded file URL
   */
  async uploadFile(file: File, options: UploadOptions = {}): Promise<string> {
    try {
      const settings = { ...DEFAULT_OPTIONS, ...options };

      // Validate file size
      if (file.size > settings.maxSize!) {
        throw new Error(
          `File size should be less than ${settings.maxSize! / (1024 * 1024)}MB`
        );
      }

      // Validate file type
      if (
        settings.allowedTypes?.length &&
        !settings.allowedTypes.includes(file.type)
      ) {
        throw new Error(
          `Please upload a valid file type: ${settings.allowedTypes.join(', ')}`
        );
      }

      const formData = new FormData();
      formData.append('file', file);

      // Add original filename
      formData.append('name', file.name);

      // Add tag if provided
      if (settings.tag) {
        formData.append('tag', settings.tag);
      }

      // Add secure upload parameters
      if (settings.secure) {
        formData.append('secure', 'true');
        if (settings.expires) {
          formData.append('expires', settings.expires.toString());
        }
      }

      // Send multipart form data
      const response = await fetchApi<UploadResponse>(
        API_ENDPOINTS.UPLOADS.BASE,
        {
          method: 'POST',
          body: formData,
          requireAuth: true, // This ensures userId is available from the JWT token
          headers: {
            Accept: 'application/json',
          },
        }
      );

      return response.result.url;
    } catch (error) {
      console.error('File upload failed:', error);
      throw error;
    }
  },
};
